import { MediaText, TextWrapping } from 'editor/src/store/design/types';

import mm2pt from 'editor/src/util/mm2pt';
import parseSvgPathToCommands from 'editor/src/util/parseSvgPathToCommands';

import { getRenderedTextObject } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

import isFontFileLoaded from './isFontFileLoaded';

// The shared SVG parser only supports one absolute operand group per command.
// Validate that exact subset before using it as evidence for a layout migration.
const numberToken = '-?\\d+(?:\\.\\d+)?';
const separator = '(?:\\s*,\\s*|\\s+|(?=-))';
const operandCounts: Record<string, number> = { M: 2, L: 2, Q: 4, C: 6, Z: 0 };
const operandPatterns = Object.fromEntries(
  Object.entries(operandCounts).map(([command, count]) => [
    command,
    new RegExp(count ? `^\\s*${numberToken}(?:${separator}${numberToken}){${count - 1}}\\s*$` : '^\\s*$'),
  ]),
);

function isCanonicalOutline(outline: string): boolean {
  if (!outline.trim().startsWith('M') || /[^MLCQZ\d.,\s-]/.test(outline)) {
    return false;
  }
  return (outline.match(/[MLCQZ][^MLCQZ]*/g) ?? []).every((segment) => {
    const operands = segment.slice(1);
    return (
      operandPatterns[segment[0]].test(operands) &&
      (operands.match(/-?\d+(?:\.\d+)?/g) ?? []).every((value) => Number.isFinite(Number(value)))
    );
  });
}

// Both renderers serialize outlines to two decimal places in mm. Compare every
// command/control point, not just the glyph bounds, to avoid mistaking another
// font or an intentionally offset imported outline for the legacy fitting bug.
function pathsMatch(first: string, second: string): boolean {
  if (!isCanonicalOutline(first) || !isCanonicalOutline(second)) {
    return false;
  }
  const left = parseSvgPathToCommands(first);
  const right = parseSvgPathToCommands(second);
  return (
    left.length > 0 &&
    left.length === right.length &&
    left.every((command, index) => {
      const other = right[index];
      return (
        command.type === other.type &&
        Object.entries(command).every(([key, value]) => {
          if (key === 'type') {
            return true;
          }
          const expected = (other as unknown as Record<string, unknown>)[key];
          return (
            typeof value === 'number' &&
            typeof expected === 'number' &&
            Number.isFinite(value) &&
            Number.isFinite(expected) &&
            Math.abs(value - expected) <= 0.02
          );
        })
      );
    })
  );
}

/** Recover only positively identified legacy /fonts/text/render fitting. */
function normalizeCustomTextRendering(element: MediaText): void {
  if (
    !element.path ||
    // Outline thickness scales with authored fontSize but is absent from SVG path matching.
    !!element.extra.stroke?.width ||
    !element.extra.fontFamily.startsWith('custom-') ||
    element.extra.customFontLayout ||
    !isFontFileLoaded(element.extra.fontFamily) ||
    (element.sx ?? 1) !== 1 ||
    (element.sy ?? 1) !== 1
  ) {
    return;
  }

  try {
    // Valid client-generated Wrap/Fit paths must keep their authored policy.
    if (pathsMatch(element.path, getRenderedTextObject(element).toSVGRelativeCoords())) {
      return;
    }
    const fitted = getRenderedTextObject({
      ...element,
      extra: { ...element.extra, wrapping: TextWrapping.Fit, customFontLayout: 'server-fit-v1' },
    });
    if (pathsMatch(element.path, fitted.toSVGRelativeCoords())) {
      element.extra = {
        ...element.extra,
        wrapping: TextWrapping.Fit,
        customFontLayout: 'server-fit-v1',
        fontSize: mm2pt(fitted.fontSize),
      };
    }
  } catch {
    // Unsupported paths/fonts retain their authored data. Never guess geometry.
  }
}

export default normalizeCustomTextRendering;
